/**
 * Command — Keyboard-driven command palette for action discovery and navigation.
 *
 * Command Component Guidelines
 * - Use Command for command palettes and quick-switchers where users search and
 *   execute actions by name. For data selection from a list, use Combobox instead.
 * - Use `CommandGroup` to organize actions into categories (e.g. "Navigation",
 *   "Actions", "Settings") — it helps users scan the palette faster.
 * - Use `CommandShortcut` inside `CommandItem` to display keyboard shortcuts
 *   alongside actions.
 * - `CommandEmpty` shows automatically when no items match the search — customize
 *   its content to guide users (e.g. "No results found. Try a different search.").
 * - Command uses the `cmdk` library (not Base UI) — its filtering and keyboard
 *   navigation work differently from Combobox.
 * @see https://cmdk.paco.me cmdk documentation
 * @see https://ui.shadcn.com/docs/components/command shadcn/ui Command documentation
 */
import type { EmptyProps } from "@replohq/lattice/components/empty/Empty";
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import * as React from "react";

import { Empty } from "@replohq/lattice/components/empty/Empty";
import { ListItem } from "@replohq/lattice/components/list-item/ListItem";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import { cn } from "@replohq/lattice/utils/cn";
import { Command as CommandPrimitive } from "cmdk";

interface CommandProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<typeof CommandPrimitive>, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function Command<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: CommandProps<ClassName>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "bg-sheet text-default rounded-2xl! p-2 flex size-full flex-col gap-2 overflow-hidden",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface CommandInputProps<ClassName extends string = string>
  extends Omit<
    React.ComponentProps<typeof CommandPrimitive.Input>,
    "className"
  > {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
}
/**
 * CommandInput is a wrapper around the CommandPrimitive.Input component that copies the style and functionality of our Input component.
 * We have to do this because CommandPrimitive.Input is not from base-ui and doesn't support the render prop.
 */
function CommandInput<ClassName extends string>({
  className,
  UNSAFE_className,
  startEnhancer,
  endEnhancer,
  ...props
}: CommandInputProps<ClassName>) {
  return (
    <div
      data-slot="command-input-wrapper"
      className={cn(
        "group flex items-center border border-component-border border-0.5 focus-within:border-default bg-sheet focus-within:shadow-2xs transition-all duration-150 w-full cursor-text has-[:disabled]:pointer-events-none has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 h-8 px-2 py-1.5 gap-2 text-sm rounded-lg m-1 mb-0 w-[calc(100%-0.5rem)]",
        className,
        UNSAFE_className,
      )}
    >
      {startEnhancer && (
        <span className="text-placeholder group-focus-within:text-default flex shrink-0 items-center transition-all duration-150 [&_svg]:size-3.5">
          {startEnhancer}
        </span>
      )}
      <CommandPrimitive.Input
        data-slot="command-input"
        className="placeholder:text-placeholder min-w-0 flex-1 bg-transparent outline-none focus:outline-none focus:ring-0"
        {...props}
      />
      {endEnhancer && (
        <span className="text-placeholder group-focus-within:text-default flex shrink-0 items-center transition-all duration-150 [&_svg]:size-3.5">
          {endEnhancer}
        </span>
      )}
    </div>
  );
}

interface CommandListProps<ClassName extends string = string>
  extends Omit<
    React.ComponentProps<typeof CommandPrimitive.List>,
    "className"
  > {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}
function CommandList<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: CommandListProps<ClassName>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        "no-scrollbar flex-1 min-h-0 scroll-py-1 outline-none overflow-x-hidden overflow-y-auto",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface CommandEmptyProps<ClassName extends string = string>
  extends Omit<
      React.ComponentProps<typeof CommandPrimitive.Empty>,
      "className"
    >,
    Omit<
      EmptyProps<ClassName>,
      "className" | "UNSAFE_className" | "title" | "description"
    > {
  title?: string;
  description?: string;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function CommandEmpty<ClassName extends string>({
  title = "No results found",
  description = "Try a different search.",
  size = "sm",
  className,
  UNSAFE_className,
  ...props
}: CommandEmptyProps<ClassName>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className={cn(
        "py-6 text-center text-sm text-muted",
        className,
        UNSAFE_className,
      )}
      {...props}
    >
      <Empty size={size} title={title} description={description} />
    </CommandPrimitive.Empty>
  );
}

interface CommandGroupProps<ClassName extends string = string>
  extends Omit<
    React.ComponentProps<typeof CommandPrimitive.Group>,
    "className"
  > {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function CommandGroup<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: CommandGroupProps<ClassName>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        "text-default **:[[cmdk-group-heading]]:text-default overflow-hidden flex flex-col gap-0.5 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1 **:[[cmdk-group-heading]]:header-h6",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  ...props
}: React.ComponentProps<typeof Separator>) {
  return <Separator className="my-1" {...props} />;
}

interface CommandItemProps<ClassName extends string = string>
  extends Omit<
    React.ComponentProps<typeof ListItem>,
    "className" | "onSelect"
  > {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  onSelect?: (value: string) => void;
  value?: string;
}

function CommandItem<ClassName extends string>({
  className,
  UNSAFE_className,
  onSelect,
  value,
  disabled,
  ...props
}: CommandItemProps<ClassName>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className="data-[selected=true]:*:bg-menu-item-selected"
      onSelect={onSelect}
      value={value}
      disabled={disabled}
    >
      <ListItem
        {...props}
        disabled={disabled}
        UNSAFE_className={cn(className, UNSAFE_className)}
      />
    </CommandPrimitive.Item>
  );
}

interface CommandShortcutProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"span">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}
function CommandShortcut<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: CommandShortcutProps<ClassName>) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        "text-placeholder group-data-selected/command-item:text-default ml-auto text-sm tracking-widest",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
